import { FiBook } from 'react-icons/fi';

export const familyPeopleData = {
  courseId: 'family-people',
  courseType: 'vocabulary',
  title: 'Family & People Vocabulary',
  icon: FiBook,
  overview: {
    impact: 'Discuss relationships and personal life fluently',
    focus: 'Family members, relationships, life stages',
    uses: 'Speaking Part 1, Listening Section 1',
    duration: '15-20 minutes'
  },
  sections: [
    {
      title: 'Family Members',
      content: `<p>Core family vocabulary:</p>`,
      points: [
        'Immediate: mother/father, brother/sister, son/daughter, husband/wife',
        'Extended: uncle/aunt, cousin, nephew/niece, grandparents, in-laws',
        'Blended: stepmother/stepfather, half-brother/half-sister'
      ]
    },
    {
      title: 'Describing People & Relationships',
      content: `<p>Useful phrases for Speaking:</p>`,
      points: [
        '\'We have a close/distant relationship.\'',
        '\'I take after my mother. (= look/act like)\'',
        '\'We get along really well. (= have good relationship)\'',
        '\'She is like a sister to me.\'',
        '\'I look up to my grandfather. (= admire)\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Word',
      type: 'select',
      instructions: 'Select the right family/people term.',
      questions: [
        {
          text: 'My mother\'s sister is my ___.',
          options: [
            'aunt',
            'cousin',
            'niece'
          ],
          answer: 'aunt',
          explanation: 'Mother\'s/father\'s sister = aunt.'
        },
        {
          text: 'My sister\'s son is my ___.',
          options: [
            'nephew',
            'cousin',
            'uncle'
          ],
          answer: 'nephew',
          explanation: 'Sister\'s/brother\'s son = nephew.'
        },
        {
          text: 'I really ___ my older brother — he\'s my role model.',
          options: [
            'look up to',
            'look after',
            'look into'
          ],
          answer: 'look up to',
          explanation: '\'Look up to\' = admire someone.'
        }
      ]
    },
    {
      title: 'Identify the Relationship',
      type: 'select',
      instructions: 'Choose the correct term for each family relationship.',
      questions: [
        {
          text: 'Your uncle\'s daughter is your ___.',
          options: ['cousin', 'niece', 'sister', 'aunt'],
          answer: 'cousin',
          explanation: 'Your uncle\'s or aunt\'s children are your cousins.'
        },
        {
          text: 'Your brother\'s daughter is your ___.',
          options: ['niece', 'cousin', 'sister', 'nephew'],
          answer: 'niece',
          explanation: 'Your brother\'s or sister\'s daughter is your niece.'
        },
        {
          text: 'Your wife\'s mother is your ___.',
          options: ['mother-in-law', 'stepmother', 'grandmother', 'aunt'],
          answer: 'mother-in-law',
          explanation: 'Your spouse\'s parents are your \'in-laws\': mother-in-law, father-in-law.'
        },
        {
          text: 'Your father\'s new wife (not your birth mother) is your ___.',
          options: ['stepmother', 'mother-in-law', 'aunt', 'half-sister'],
          answer: 'stepmother',
          explanation: 'A stepmother is a woman married to your father who is not your biological mother.'
        }
      ]
    },
    {
      title: 'Complete Family Descriptions',
      type: 'fill-in',
      instructions: 'Type the correct word or phrase to complete each sentence.',
      questions: [
        {
          text: 'I really ___ after my father — we look exactly alike.',
          answer: 'take',
          explanation: '\'Take after\' means to resemble a family member in appearance or personality.'
        },
        {
          text: 'My sister and I ___ along very well. We never argue.',
          answer: 'get',
          explanation: '\'Get along (with)\' means to have a good, friendly relationship with someone.'
        },
        {
          text: 'She is an only ___. She doesn\'t have any brothers or sisters.',
          answer: 'child',
          explanation: 'An \'only child\' is someone with no siblings.'
        },
        {
          text: 'My grandparents have been married for 50 years. They are still very close and ___ each other deeply.',
          answer: 'love',
          explanation: 'Describing long-lasting family bonds: \'love each other deeply\' shows strong affection.'
        }
      ]
    },
    {
      title: 'Describe Your Family',
      type: 'writing',
      instructions: 'Write 1-2 sentences for each prompt, as if answering an IELTS Speaking Part 1 question.',
      questions: [
        {
          text: 'Do you have a large family? Tell me about them.',
          answer: 'family, siblings, parents',
          explanation: 'Example: \'I come from a fairly large family — I have two older brothers and a younger sister. We all get along really well.\' Use family vocabulary and relationship phrases.'
        },
        {
          text: 'Who are you closest to in your family? Why?',
          answer: 'close, look up to, get along',
          explanation: 'Example: \'I\'m closest to my mother because she\'s incredibly patient and always supports me. I really look up to her.\' Use personality adjectives and relationship phrases.'
        },
        {
          text: 'Do you take after anyone in your family?',
          answer: 'take after, similar, personality',
          explanation: 'Example: \'People say I take after my father — we have the same sense of humour and we\'re both quite outgoing.\' Use \'take after\' correctly and add personality details.'
        }
      ]
    }
  ],
  tips: [
    'Learn family terms in pairs: uncle/aunt, nephew/niece',
    'Practice describing your family using varied vocabulary',
    'Learn phrasal verbs: get along with, take after, look up to',
    'Family is one of the most common Speaking Part 1 topics'
  ],
  dailyPractice: [
    'Practice family & people vocabulary concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Family & People Vocabulary: Family members, relationships, and life stages. Essential for IELTS Speaking Part 1.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...familyPeopleData,
      title: familyPeopleData.title + ' (Foundations)',
      overview: { ...familyPeopleData.overview, duration: '15-20 minutes' },
      sections: familyPeopleData.sections.slice(0, Math.max(2, Math.ceil(familyPeopleData.sections.length / 2))),
      exercises: familyPeopleData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...familyPeopleData,
      title: familyPeopleData.title + ' (Advanced)',
      overview: { ...familyPeopleData.overview, duration: '30-35 minutes' },
      sections: familyPeopleData.sections,
      exercises: familyPeopleData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(familyPeopleData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return familyPeopleData;
}
